'use client';

import { useEffect, useRef, useState } from 'react';
import { useAccountSwitcherContext } from './AccountSwitcherProvider';

export interface AccountSwitcherProps {
  className?: string;
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  width?: number;
  height?: number;
  /** Callback when iframe height changes (auto-resize) */
  onHeightChange?: (height: number) => void;
}

/**
 * AccountSwitcher iframe component
 * Displays the account switcher widget in an iframe
 */
export function AccountSwitcher({
  className = '',
  width = 320,
  height: initialHeight = 400,
  onHeightChange,
}: AccountSwitcherProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [embedUrl, setEmbedUrl] = useState<string | null>(null);
  const { registerIframe } = useAccountSwitcherContext();

  // Fetch widget token from backend (backend should implement this endpoint)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const fetchToken = async () => {
      try {
        // This endpoint should be implemented in the Next.js app
        // It calls the backend API to get the widget token
        const response = await fetch('/api/account-switcher/token');

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        if (!data.embed_url) {
          throw new Error('No embed_url in response');
        }

        setEmbedUrl(data.embed_url);
        setIsLoading(false);
      } catch (err) {
        setError(err as Error);
        setIsLoading(false);
      }
    };

    fetchToken();
  }, []);

  // Register iframe with PostMessageClient when it loads
  useEffect(() => {
    if (!iframeRef.current || !embedUrl) return;

    const iframe = iframeRef.current;

    // Extract origin from embed URL
    const url = new URL(embedUrl);
    const origin = url.origin;

    // Register iframe after it loads
    const handleLoad = () => {
      registerIframe(iframe, origin);
    };

    iframe.addEventListener('load', handleLoad);

    return () => {
      iframe.removeEventListener('load', handleLoad);
    };
  }, [embedUrl, registerIframe]);

  // Listen for resize messages from iframe
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleMessage = (event: MessageEvent) => {
      // Check if message is from our iframe
      if (!embedUrl) return;

      const url = new URL(embedUrl);
      if (event.origin !== url.origin) return;

      const message = event.data;
      if (!message || message.type !== 'SIZE_CHANGED') return;

      const payload = message.payload || message;
      if (payload.height && iframeRef.current) {
        // Use direct DOM manipulation instead of state update to avoid re-render
        iframeRef.current.style.height = `${payload.height}px`;
        onHeightChange?.(payload.height);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [embedUrl, onHeightChange]);

  if (isLoading) {
    return (
      <div
        className={`${className} flex items-center justify-center`}
        style={{ width, height: initialHeight }}
      >
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`${className} flex items-center justify-center text-red-500`}
        style={{ width, height: initialHeight }}
      >
        <p>Failed to load account switcher</p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`${className} relative`}>
      <iframe
        ref={iframeRef}
        src={embedUrl || ''}
        style={{ width, height: initialHeight }}
        className="border-0 rounded-lg shadow-lg"
        title="Account Switcher"
        sandbox="allow-same-origin allow-scripts allow-forms"
      />
    </div>
  );
}
